import React, { useEffect, useState, useRef, useCallback } from 'react'
// import HeaderHome from '../../components/HeaderHome'
import { motion } from 'framer-motion'
import { IoIosArrowForward } from 'react-icons/io'
// import { HiArrowRight } from 'react-icons/hi'
import { itemVariants, containerVariants } from '../../components/data/motionData'
import { getArticles } from '../../hooks/articles'
import { redirectApp } from '../../../core/utils/helper'
// import dayjs from 'dayjs';

const Articles = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const articlesContainerRef = useRef(null);
  const dragStateRef = useRef({ isDragging: false, startX: 0, scrollLeft: 0 });
  const didDragRef = useRef(false);
  const [articlesData, setArticlesData] = useState([]);
  const featuredArticles = articlesData.slice(0, 4);

  useEffect(() => {
    const fetchArticles = async () => {
      const articles = await getArticles();
      if (articles) {
        setArticlesData(articles);
      }
    };
    fetchArticles();
  }, []);

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 500);
      setIsTablet(width > 500 && width <= 780);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  const handleDragStart = useCallback((event) => {
    const container = articlesContainerRef.current;
    if (!container || (event.pointerType === 'mouse' && event.button !== 0)) return;

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
    };
    didDragRef.current = false;
    container.setPointerCapture(event.pointerId);
  }, []);

  const handleDragMove = useCallback((event) => {
    const container = articlesContainerRef.current;
    if (!container || !dragStateRef.current.isDragging) return;

    const delta = event.clientX - dragStateRef.current.startX;
    if (Math.abs(delta) > 5) {
      didDragRef.current = true;
    }

    container.scrollLeft = dragStateRef.current.scrollLeft - delta;
  }, []);

  const handleDragEnd = useCallback((event) => {
    const container = articlesContainerRef.current;
    if (!container || !dragStateRef.current.isDragging) return;

    dragStateRef.current.isDragging = false;

    if (container.hasPointerCapture?.(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleArticleClick = useCallback((event) => {
    if (didDragRef.current) {
      event.preventDefault();
    }
  }, []);

  const convertToExcerpt = (content) => {
    const textOnly = content.replace(/<[^>]*>/g, '');
    const cleanText = textOnly.replace(/\s+/g, ' ').trim();
    return cleanText.substring(0, 100) + '...';
  }

  const getExcerptLines = (article) => {
    const raw = article.excerpt || convertToExcerpt(article.content).replace(/\.\.\.$/, '');
    const midpoint = Math.min(72, Math.floor(raw.length / 2));
    const splitAt = raw.lastIndexOf(' ', midpoint) > 0 ? raw.lastIndexOf(' ', midpoint) : midpoint;

    return {
      primary: raw.length > splitAt ? `${raw.slice(0, splitAt).trim()}...` : raw,
      secondary: raw.length > splitAt ? `${raw.slice(splitAt).trim().slice(0, 72)}...` : '',
    };
  };

  const getFeaturedImageUrl = (article) =>
    article.featured_image?.url ||
    article.featured_image?.formats?.medium?.url ||
    article.featured_image?.formats?.small?.url;

  const FeaturedArticleCard = ({ article, showTag = false, onClick }) => {
    const { primary } = getExcerptLines(article);
    const categoryName = article.categories?.[0]?.name;

    return (
      <motion.a
        href={`https://ai-pro.org/learn-ai/articles/${article.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className="group relative block aspect-[3/4] overflow-hidden rounded-2xl"
        variants={itemVariants}
        whileTap={{ scale: 0.98 }}
      >
        <img
          src={getFeaturedImageUrl(article)}
          alt={article.featured_image?.name || article.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/10" />
        <div className="absolute inset-x-0 bottom-0 flex flex-col p-4 sm:p-5">
          {showTag && categoryName && (
            <span className="mb-3 self-start rounded-md bg-white/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
              {categoryName}
            </span>
          )}
          <h3 className="mb-2 line-clamp-2 text-base font-bold text-white sm:text-lg !leading-[20px]">
            {article.title}
          </h3>
          <p className="line-clamp-1 text-[14px] leading-relaxed text-[#FFFFFFE6]">
            {primary}
          </p>
        </div>
      </motion.a>
    );
  };

  return (
    <div className="mx-auto flex flex-col items-center px-10 max-w-480:px-0 pt-12 max-w-480:pt-12 pb-24 max-w-480:pb-2 h-full w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
      <p className="text-black text-[36px] font-semibold font-['Inter'] text-center">Your AI Learning Hub</p>
      <p className="text-gray-600 mx-[10px] text-[16px] font-normal font-inter max-w-2xl lg:mb-8 text-center mt-4 mb-[35px]">
      Browse helpful articles, how-tos, and real-world examples of how AI is being used today — at work, in learning, and in everyday tasks. No hype, no noise. Just useful info you can actually apply.
      </p>
      <button className="bg-[#3073D5] hover:bg-[#2590EE] flex items-center text-white pl-[16px] pr-[12px] py-3 rounded-full font-semibold transition-all duration-300 transform mb-12" 
        onClick={(e) => redirectApp(e, 'ai-pro.org/learn-ai/articles', 'app')}>
        Learn more
        <IoIosArrowForward className="ml-1 text-lg" />
      </button>

      {isMobile || isTablet ? (
        <motion.div
          ref={articlesContainerRef}
          className={`flex w-full no-scrollbar overflow-x-auto gap-[10px] pb-12 ${
            isMobile ? '' : 'cursor-grab active:cursor-grabbing touch-pan-x select-none'
          }`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          {...(!isMobile && {
            onPointerDown: handleDragStart,
            onPointerMove: handleDragMove,
            onPointerUp: handleDragEnd,
            onPointerCancel: handleDragEnd,
          })}
        >
          {featuredArticles.map((article, index) => (
            <div
              key={article.id || article.documentId || index}
              className={`${index === 0 ? 'ml-[10px]' : ''} ${index === featuredArticles.length - 1 ? 'mr-[10px]' : ''} w-[78%] max-w-[300px] shrink-0`}
            >
              <FeaturedArticleCard
                article={article}
                showTag={index === 0}
                onClick={handleArticleClick}
              />
            </div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          className="grid w-full max-w-6xl gap-[10px] pb-12 max-w-480:w-[80%] grid-cols-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {featuredArticles.map((article, index) => (
            <FeaturedArticleCard
              key={article.id || article.documentId || index}
              article={article}
              showTag={index === 0}
            />
          ))}
        </motion.div>
      )}
    </div>
  )
}

export default Articles;